import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { MapContainer, TileLayer, Circle, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "leaflet.heat";
import { http } from "@/lib/api";
import { toast } from "sonner";
import ListenTranslateBar from "@/components/ListenTranslateBar";
import ShareButton from "@/components/ShareButton";
import PestAlertPanel from "@/components/PestAlertPanel";
import { ArrowLeft, Bug, MapPin, Warning, Sparkle, CaretDown } from "@phosphor-icons/react";

/**
 * FarmersPestHeatmap — /farmers/pest-heatmap
 *
 * A privacy-preserving pest & disease pressure heatmap. Farmers see risk
 * building up nearby before it reaches their fields. All coordinates are
 * snapped server-side to a 2-km grid so private farm locations never leak.
 */

const RADIUS_CHOICES = [10, 25, 50, 100, 250];
const DAYS_CHOICES = [7, 14, 30, 90];
const CATEGORY_CHOICES = ["pest", "disease", "insect"];

function HeatLayer({ points }) {
  const map = useMap();
  useEffect(() => {
    if (!points || points.length === 0) return;
    const maxWeight = points.reduce((m, p) => Math.max(m, p.weight), 1);
    const pts = points.map((p) => [p.lat, p.lon, Math.min(1, p.weight / maxWeight)]);
    const layer = L.heatLayer(pts, {
      radius: 32, blur: 26, maxZoom: 6, minOpacity: 0.4,
      gradient: { 0.2: "#0284c7", 0.4: "#16a34a", 0.6: "#ca8a04", 0.8: "#c2410c", 1.0: "#be123c" },
    });
    layer.addTo(map);
    return () => { map.removeLayer(layer); };
  }, [points, map]);
  return null;
}

function CenterOnLocation({ lat, lon, radiusKm }) {
  const map = useMap();
  useEffect(() => {
    if (lat === null || lon === null) return;
    map.setView([lat, lon], radiusKm <= 25 ? 9 : radiusKm <= 100 ? 7 : 5);
  }, [lat, lon, radiusKm, map]);
  return null;
}

export default function FarmersPestHeatmap() {
  const [loc, setLoc] = useState(() => {
    try { const raw = localStorage.getItem("np:pulse-location"); return raw ? JSON.parse(raw) : null; }
    catch { return null; }
  });
  const [radiusKm, setRadiusKm] = useState(100);
  const [days, setDays] = useState(30);
  const [category, setCategory] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!loc?.lat) return;
    setLoading(true);
    const params = new URLSearchParams({
      latitude: String(loc.lat),
      longitude: String(loc.lon),
      radius_km: String(radiusKm),
      days: String(days),
    });
    if (category) params.set("category", category);
    http.get(`/farmers/pest-heatmap?${params.toString()}`)
      .then(({ data }) => setData(data))
      .catch(() => toast.error("Could not load heatmap"))
      .finally(() => setLoading(false));
  }, [loc?.lat, loc?.lon, radiusKm, days, category]);

  const useMyLocation = () => {
    if (!navigator.geolocation) return toast.error("Geolocation is not supported here.");
    navigator.geolocation.getCurrentPosition((p) => {
      const l = { lat: p.coords.latitude, lon: p.coords.longitude, label: "My location" };
      setLoc(l);
      try { localStorage.setItem("np:pulse-location", JSON.stringify(l)); } catch { /* noop */ }
    }, () => toast.error("Could not read your location."));
  };

  const heroText = `Farmers Pulse pest and disease pressure heatmap. See risk rising ${radiusKm} kilometers from your farm before it reaches your fields. Coordinates are aggregated to a 2 kilometer grid so private farm locations never leak.`;

  const points = data?.points || [];
  const summary = data?.summary || {};
  const totalReports = summary.total_reports || 0;

  return (
    <main className="bg-[#F5EBDC] min-h-screen pt-32 md:pt-36 pb-32 px-5 md:px-10" data-testid="farmers-pest-heatmap-page">
      <div className="max-w-[1400px] mx-auto">
        <Link to="/farmers" className="inline-flex items-center gap-1.5 font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[#3A3830] hover:text-[#1B4332]">
          <ArrowLeft size={11} weight="bold" /> Farmers Pulse
        </Link>
        <div className="mt-3 font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[color:var(--np-brand,#B85F2A)] flex items-center gap-2">
          <Bug size={12} weight="fill" /> / Pest & disease pressure
        </div>
        <h1 className="mt-2 font-serif-display text-4xl md:text-5xl leading-[0.98] tracking-tight">
          {totalReports > 0 ? `${totalReports} nearby reports · past ${days} days` : "No pest reports near you yet."}
        </h1>
        <p className="mt-3 max-w-2xl text-sm text-[#3A3830]">
          Aggregated from farmer reports and NaturePulse observations within your radius. All coordinates snap to a 2 km grid so private farm locations never leak.
        </p>

        <div className="mt-4 flex flex-wrap items-center gap-2">
          <ListenTranslateBar text={heroText} testidPrefix="pest-heatmap-tools" />
          <ShareButton title="Farmers Pulse — pest heatmap" text={heroText} variant="chip" testid="pest-heatmap-share" />
          <button
            onClick={useMyLocation}
            data-testid="pest-heatmap-use-location"
            className="inline-flex items-center gap-1.5 rounded-full border border-black/20 bg-white px-4 py-2 font-mono-tech text-[10px] tracking-[0.28em] uppercase hover:bg-black/5"
          >
            <MapPin size={11} weight="fill" /> {loc?.lat ? "Update location" : "Use my location"}
          </button>
        </div>

        {/* Controls */}
        <div className="mt-6 rounded-2xl border border-black/10 bg-white p-4 flex flex-wrap gap-4 items-center" data-testid="pest-heatmap-controls">
          <ChipGroup label="Radius" testidPrefix="pest-radius" options={RADIUS_CHOICES.map((r) => ({ key: r, label: `${r} km` }))} value={radiusKm} onChange={setRadiusKm} />
          <ChipGroup label="Time window" testidPrefix="pest-days" options={DAYS_CHOICES.map((d) => ({ key: d, label: `${d} d` }))} value={days} onChange={setDays} />
          <ChipGroup label="Category" testidPrefix="pest-category" options={[{ key: "", label: "All" }, ...CATEGORY_CHOICES.map((c) => ({ key: c, label: c }))]} value={category} onChange={setCategory} />
        </div>

        {/* Layout: map + side panel */}
        <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-6" data-testid="pest-heatmap-body">
          {/* Map */}
          <div className="lg:col-span-2 rounded-2xl border border-black/10 overflow-hidden bg-white h-[480px]">
            {!loc?.lat ? (
              <div className="w-full h-full grid place-items-center text-center p-8" data-testid="pest-heatmap-no-location">
                <div>
                  <MapPin size={22} weight="fill" className="mx-auto text-[color:var(--np-brand,#B85F2A)] mb-2" />
                  <div className="font-serif-display text-2xl">Set your location to see the heatmap.</div>
                </div>
              </div>
            ) : (
              <div className="w-full h-full" data-testid="pest-heatmap-map">
                <MapContainer
                  center={[loc.lat, loc.lon]}
                  zoom={7}
                  scrollWheelZoom
                  style={{ width: "100%", height: "100%" }}
                >
                  <TileLayer
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    attribution='&copy; OpenStreetMap contributors'
                  />
                  <CenterOnLocation lat={loc.lat} lon={loc.lon} radiusKm={radiusKm} />
                  <Circle center={[loc.lat, loc.lon]} radius={radiusKm * 1000} pathOptions={{ color: "#B85F2A", weight: 1.5, opacity: 0.7, fillOpacity: 0.03 }} />
                  <HeatLayer points={points} />
                </MapContainer>
              </div>
            )}
          </div>

          {/* Side panel */}
          <aside className="rounded-2xl border border-black/10 bg-white p-5" data-testid="pest-heatmap-side">
            <div className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[#3A3830]">Insights</div>
            {loading && <div className="mt-3 text-sm text-[#3A3830]">Loading…</div>}
            {!loading && loc?.lat && totalReports === 0 && (
              <div className="mt-3 text-sm text-[#3A3830]">
                No pest, disease or insect observations within {radiusKm} km in the last {days} days. Be the first — <Link to="/farmers/report" className="text-[color:var(--np-brand,#B85F2A)] underline">file a report</Link>.
              </div>
            )}
            {totalReports > 0 && (
              <>
                <div className="mt-3 grid grid-cols-2 gap-2">
                  <Stat label="Reports" value={totalReports} />
                  <Stat label="Active days" value={summary.active_days || 0} />
                </div>
                {(summary.top_pests || []).length > 0 && (
                  <div className="mt-4">
                    <div className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[#3A3830]">Top pests / issues</div>
                    <ul className="mt-2 space-y-1">
                      {(summary.top_pests || []).map((p) => (
                        <li key={p.key} className="flex items-center justify-between text-sm">
                          <span className="truncate">{p.key}</span>
                          <span className="ml-2 font-mono-tech text-[10px] tracking-[0.22em] uppercase text-[color:var(--np-brand,#B85F2A)]">×{p.count}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {(summary.top_crops || []).length > 0 && (
                  <div className="mt-4">
                    <div className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[#3A3830]">Affected crops</div>
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {(summary.top_crops || []).map((c) => (
                        <span key={c.key} className="rounded-full bg-[#FBF2E1] border border-black/10 px-2 py-0.5 font-mono-tech text-[9px] tracking-[0.22em] uppercase">
                          {c.key} · {c.count}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                {(summary.severity_mix || []).length > 0 && (
                  <div className="mt-4">
                    <div className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[#3A3830]">Severity mix</div>
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {(summary.severity_mix || []).map((s) => (
                        <span key={s.key} className="rounded-full bg-white border border-black/15 px-2 py-0.5 font-mono-tech text-[9px] tracking-[0.22em] uppercase capitalize">
                          {s.key} · {s.count}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                <TrendSparkline trend={data?.trend || []} />
              </>
            )}
            <div className="mt-6 rounded-lg border border-amber-200 bg-amber-50 text-amber-900 p-3 text-xs" data-testid="pest-heatmap-disclaimer">
              <Warning size={12} weight="fill" className="inline mr-1" />
              This heatmap aggregates public farmer observations. Not a substitute for extension advice.
            </div>
          </aside>
        </div>

        {/* Top cells table */}
        {points.length > 0 && (
          <section className="mt-8" data-testid="pest-heatmap-cells">
            <h2 className="font-serif-display text-2xl">Hotspots</h2>
            <div className="mt-3 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
              {points.slice(0, 9).map((p, idx) => (
                <div key={idx} className="rounded-2xl border border-black/10 bg-white p-4" data-testid={`pest-heatmap-cell-${idx}`}>
                  <div className="flex items-start justify-between">
                    <div className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[color:var(--np-brand,#B85F2A)] flex items-center gap-1">
                      <Bug size={11} weight="fill" /> Cell #{idx + 1} · {p.weight} reports
                    </div>
                    <div className="text-[10px] font-mono-tech tracking-[0.22em] uppercase text-[#3A3830]">
                      {p.lat.toFixed(2)}, {p.lon.toFixed(2)}
                    </div>
                  </div>
                  {(p.top_pests || []).length > 0 && (
                    <div className="mt-2 text-sm text-[#1B4332]">
                      {p.top_pests.map((x) => x.key).slice(0, 3).join(" · ")}
                    </div>
                  )}
                  {(p.sample_titles || []).length > 0 && (
                    <ul className="mt-2 text-xs text-[#3A3830] list-disc list-inside space-y-0.5">
                      {p.sample_titles.slice(0, 2).map((t, i) => <li key={i} className="line-clamp-1">{t}</li>)}
                    </ul>
                  )}
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Alert subscription panel */}
        <section className="mt-8">
          <PestAlertPanel
            latitude={loc?.lat ?? null}
            longitude={loc?.lon ?? null}
            radiusKm={radiusKm}
            days={days}
            category={category || null}
          />
        </section>
      </div>
    </main>
  );
}

function ChipGroup({ label, options, value, onChange, testidPrefix }) {
  return (
    <div className="flex items-center gap-2 flex-wrap">
      <span className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[#3A3830]">{label}</span>
      {options.map((o) => {
        const active = value === o.key;
        return (
          <button
            key={String(o.key)}
            onClick={() => onChange(o.key)}
            data-testid={`${testidPrefix}-${String(o.key) || "all"}`}
            className={`rounded-full border px-3 py-1 font-mono-tech text-[10px] tracking-[0.22em] uppercase ${
              active ? "bg-[#241209] text-[#F5EBDC] border-[#241209]" : "bg-white border-black/15 hover:border-black/40"
            }`}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="rounded-lg border border-black/10 bg-[#FBF2E1]/40 p-3">
      <div className="font-mono-tech text-[9px] tracking-[0.28em] uppercase text-[#3A3830]">{label}</div>
      <div className="mt-1 font-serif-display text-xl">{value}</div>
    </div>
  );
}

function TrendSparkline({ trend }) {
  const path = useMemo(() => {
    if (!trend || trend.length < 2) return null;
    const max = Math.max(...trend.map((t) => t.count), 1);
    const step = 100 / (trend.length - 1);
    return trend
      .map((t, i) => `${i === 0 ? "M" : "L"} ${(i * step).toFixed(2)} ${(30 - (t.count / max) * 26).toFixed(2)}`)
      .join(" ");
  }, [trend]);
  if (!path) return null;
  return (
    <div className="mt-4" data-testid="pest-heatmap-trend">
      <div className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[#3A3830]">Daily trend</div>
      <svg viewBox="0 0 100 30" preserveAspectRatio="none" className="mt-1 w-full h-12">
        <path d={path} fill="none" stroke="#B85F2A" strokeWidth={0.8} strokeLinejoin="round" strokeLinecap="round" />
      </svg>
    </div>
  );
}
